import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, SELLER_PRODUCT_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSharedLocation } from "@/context/LocationContext";
import LocationFilterBar, { UnknownLocationHeading, DistanceBadge } from "@/components/LocationFilterBar";
import { SaveToListButton, useSavedItems } from "@/components/SaveToList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Store, MapPin } from "lucide-react";

export default function Marketplace() {
  const { user } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const { loc, radius } = useSharedLocation();
  const { isParent, saved, add } = useSavedItems();
  const unknownStart = sellers.findIndex((s) => s.distance_km == null);

  const load = () => {
    let stale = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (loc) {
      params.set("lat", loc.lat);
      params.set("lng", loc.lng);
      params.set("radius_km", radius);
    }
    api.get(`/marketplace-sellers?${params.toString()}`)
      .then((r) => { if (!stale) setSellers(r.data); })
      .catch(() => {})
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  };
  useEffect(load, [search, category, loc, radius]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="marketplace">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sellers by name, category or location..."
            className="pl-11 rounded-full bg-white/80 border-sage/30 h-12"
            data-testid="marketplace-search-input"
          />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-1 px-1" data-testid="marketplace-filters">
        <Button size="sm" onClick={() => setCategory("")}
          className={`rounded-full shrink-0 ${!category ? "bg-sage text-white hover:bg-sage-dark" : "bg-white/70 text-forest hover:bg-sage-light border border-sage/30"}`}
          data-testid="mkt-filter-all">
          All
        </Button>
        {SELLER_PRODUCT_CATEGORIES.map((c) => (
          <Button key={c} size="sm" onClick={() => setCategory(category === c ? "" : c)}
            className={`rounded-full shrink-0 ${category === c ? "bg-sage text-white hover:bg-sage-dark" : "bg-white/70 text-forest hover:bg-sage-light border border-sage/30"}`}
            data-testid={`mkt-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {c}
          </Button>
        ))}
      </div>

      <LocationFilterBar testPrefix="marketplace" noun="sellers" />

      {loading ? (
        <div className="text-center py-20 text-forest/50">Loading marketplace...</div>
      ) : sellers.length === 0 ? (
        <div className="text-center py-20 bg-white/60 border border-sage/20 rounded-3xl" data-testid="marketplace-empty">
          <Store className="w-10 h-10 text-sage mx-auto mb-3" />
          <p className="text-forest/60">No sellers listed yet. Check back soon!</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sellers.map((s, i) => {
            const mine = user && user.user_id === s.seller_id;
            return (
              <Fragment key={s.seller_id}>
              {loc && i === unknownStart && <UnknownLocationHeading testid="marketplace-unknown-location-heading" />}
              <Link to={`/marketplace-store/${s.seller_id}`}
                className={`fade-in-${Math.min(i % 4, 3)} group relative bg-white/80 border border-sage/20 rounded-3xl overflow-hidden shadow-sm hover:shadow-md hover:scale-[1.02] transition-[transform,box-shadow]`}
                data-testid={`mkt-seller-card-${s.seller_id}`}>
                {mine && (
                  <span className="absolute top-3 left-3 z-10 text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2 py-1 rounded-full shadow" data-testid={`mkt-seller-own-badge-${s.seller_id}`}>
                    Your business
                  </span>
                )}
                <div className="h-40 bg-sage-light flex items-center justify-center overflow-hidden">
                  {s.business_logo ? (
                    <img src={fileUrl(s.business_logo)} alt={s.business_name} className="w-full h-full object-contain" />
                  ) : (
                    <img src="/logo.png" alt="Compass Kids logo" className="max-h-24 max-w-[70%] object-contain opacity-80" data-testid={`mkt-seller-logo-fallback-${s.seller_id}`} />
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading font-bold text-forest leading-snug" data-testid={`mkt-seller-name-${s.seller_id}`}>{s.business_name || "Unnamed seller"}</h3>
                    <span className="shrink-0 text-[11px] font-black text-sage-dark bg-sage-light px-2 py-1 rounded-full" data-testid={`mkt-seller-count-${s.seller_id}`}>
                      {s.listing_count} {s.listing_count === 1 ? "product" : "products"}
                    </span>
                  </div>
                  {s.distance_km != null && <div className="mt-2"><DistanceBadge km={s.distance_km} testid={`mkt-seller-distance-${s.seller_id}`} /></div>}
                  {s.tagline && <p className="text-sm text-forest/70 mt-1" data-testid={`mkt-seller-tagline-${s.seller_id}`}>{s.tagline}</p>}
                  {s.location && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location)}`, "_blank", "noopener,noreferrer"); }}
                      className="flex items-center gap-1 text-xs text-sage-dark hover:text-terracotta-dark hover:underline mt-2 transition-colors"
                      data-testid={`mkt-seller-location-${s.seller_id}`}
                      aria-label={`Open ${s.location} in Google Maps`}
                    >
                      <MapPin className="w-3.5 h-3.5" /> {s.location}
                    </button>
                  )}
                  {(s.product_categories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3" data-testid={`mkt-seller-cats-${s.seller_id}`}>
                      {s.product_categories.slice(0, 3).map((c) => (
                        <span key={c} className="text-[10px] font-bold uppercase tracking-wide bg-cream text-forest/70 border border-sage/20 px-2 py-0.5 rounded-full">{c}</span>
                      ))}
                      {s.product_categories.length > 3 && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-forest/50 px-1 py-0.5">+{s.product_categories.length - 3}</span>
                      )}
                    </div>
                  )}
                  {isParent && <SaveToListButton itemType="marketplace" itemId={s.seller_id} saved={saved} add={add} />}
                </div>
              </Link>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
